import React from 'react';
import './MobileAIMode.css';

/**
 * Mobile-optimized AI Mode Selector Component
 * Supports both Agent and Plan modes with touch-friendly interface
 * 
 * @param {Object} props
 * @param {string} props.aiMode - Current AI mode ('agent' or 'plan')
 * @param {Function} props.onModeChange - Callback when mode changes
 * @param {boolean} props.isOpen - Whether dropdown is open
 * @param {Function} props.onToggle - Callback to toggle dropdown
 */
function MobileAIMode({ aiMode, onModeChange, isOpen, onToggle }) {
  const modes = [
    {
      value: 'agent',
      icon: '🤖',
      label: 'Agent',
      description: 'AI can create projects and code',
      color: '#6366f1'
    },
    {
      value: 'plan',
      icon: '📋',
      label: 'Plan',
      description: 'Provides information only',
      color: '#10b981'
    }
  ];

  const currentMode = modes.find(m => m.value === aiMode) || modes[0];

  const handleModeSelect = (mode) => {
    onModeChange(mode);
    onToggle(false);
  };

  return (
    <div className="mobile-ai-mode">
      <button
        className="mobile-mode-toggle"
        onClick={() => onToggle(!isOpen)}
        type="button"
        aria-label={`Current mode: ${currentMode.label}`}
        aria-expanded={isOpen}
      >
        <span className="mobile-mode-icon" role="img" aria-label={currentMode.label}>
          {currentMode.icon}
        </span>
        <span className="mobile-mode-label">{currentMode.label}</span>
        <svg 
          className={`mobile-dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="16" 
          height="16" 
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path 
            d="M4 6l4 4 4-4" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          <div 
            className="mobile-mode-backdrop"
            onClick={() => onToggle(false)}
            aria-hidden="true"
          />
          <div className="mobile-mode-dropdown" role="menu">
            {modes.map((mode) => (
              <button
                key={mode.value}
                className={`mobile-mode-option ${aiMode === mode.value ? 'active' : ''}`}
                onClick={() => handleModeSelect(mode.value)}
                type="button"
                role="menuitem"
                aria-label={`${mode.label}: ${mode.description}`}
              >
                <span 
                  className="mobile-mode-option-icon" 
                  role="img" 
                  aria-label={mode.label}
                >
                  {mode.icon}
                </span>
                <div className="mobile-mode-option-text">
                  <span className="mobile-mode-option-label">{mode.label}</span>
                  <span className="mobile-mode-option-desc">{mode.description}</span>
                </div>
                {aiMode === mode.value && (
                  <svg 
                    className="mobile-mode-check"
                    width="20" 
                    height="20" 
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path 
                      d="M16 6l-8 8-4-4" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default MobileAIMode;
